import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import Card from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { Icon, InputAdornment } from '@mui/material';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import bgImage from 'assets/images/bg-sign-in-basic.jpeg';
import { AuthBasicLayout } from '../layouts/AuthBasicLayout';
import { authApi } from '../api/authApi';
import { uiBus } from 'shared/api/uiBus';

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
}
function unwrapData(resp) {
  return resp?.data ?? resp;
}
function toUiMessage(e) {
  const status = e?.response?.status ?? e?.status;
  const data = e?.response?.data ?? e?.data;

  if (data?.errors && typeof data.errors === 'object') {
    const joined = Object.values(data.errors).filter(Boolean).join(' • ');
    if (joined) return joined;
  }

  // Forgot password akışında “hesap yok” gibi mesajları göstermeyelim (enumeration)
  if (status === 404) return 'Eğer hesap mevcutsa, yenileme bağlantısı gönderilecek.';

  return data?.message ?? e?.message ?? 'İşlem tamamlanamadı. Lütfen tekrar deneyin.';
}

export default function ForgotPasswordPage() {
  const [loginId, setLoginId] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // başarı ekranı
  const [done, setDone] = useState(false);
  const [masked, setMasked] = useState(''); // opsiyonel

  const canSubmit = useMemo(() => {
    return loginId.trim().length > 0 && !loading;
  }, [loginId, loading]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr(null);

    const id = loginId.trim();
    if (!id) {
      const m = 'Lütfen e-posta adresinizi veya kullanıcı adınızı girin.';
      setErr(m);
      uiBus.emit('toast', { type: 'error', message: m });
      return;
    }

    setLoading(true);
    try {
      // opsiyonel: kullanıcı adı girdiyse email mask göstermek için resolve dene
      if (!isValidEmail(id)) {
        try {
          const r0 = await authApi.resolveLoginId({ loginId: id });
          const d0 = unwrapData(r0);
          setMasked(d0?.emailMasked || '');
        } catch (_) {
          // sessiz geç
        }
      } else {
        setMasked(id);
      }

      await authApi.forgotPassword({ loginId: id });

      // ✅ güvenlik: her durumda aynı sonuç ekranı
      setDone(true);
      uiBus.emit('toast', {
        type: 'success',
        message: 'Eğer hesap mevcutsa, şifre yenileme bağlantısı e-postana gönderildi.',
      });
    } catch (e2) {
      const msg = toUiMessage(e2);
      // burada bile “hesap yok” sızdırma, mesajı gene genel tut
      setErr(msg);
      uiBus.emit('toast', { type: 'error', message: msg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthBasicLayout image={bgImage} footerVariant="light">
      <Card
        sx={{
          overflow: 'hidden',
          borderRadius: { xs: '18px', sm: '22px' },
          width: '100%',
          maxWidth: 520,
          backgroundColor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.55)',
          boxShadow: '0 30px 90px rgba(0,0,0,0.22)',
        }}
      >
        {/* Header */}
        <MDBox
          px={{ xs: 2.5, sm: 3.25 }}
          pt={{ xs: 2.75, sm: 3.25 }}
          pb={{ xs: 2.0, sm: 2.25 }}
          textAlign="left"
          sx={{
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(46,139,255,0.16), rgba(43,83,134,0.06))',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.25}>
            <MDBox
              width={44}
              height={44}
              sx={{
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(135deg, #2b5386, #2E8BFF)',
                boxShadow: '0 12px 30px rgba(46,139,255,0.25)',
              }}
            >
              <Icon sx={{ color: '#fff', fontSize: 22 }}>lock_reset</Icon>
            </MDBox>

            <MDBox>
              <MDTypography variant="h5" fontWeight="bold" color="dark" sx={{ lineHeight: 1.1 }}>
                Şifre yenile
              </MDTypography>
              <MDTypography variant="button" color="text" sx={{ opacity: 0.7 }}>
                E-posta veya kullanıcı adını gir, link gönderelim.
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Body */}
        <MDBox pt={{ xs: 2.25, sm: 3 }} pb={{ xs: 2.25, sm: 3 }} px={{ xs: 2.25, sm: 3 }}>
          {done ? (
            <MDBox sx={{ display: 'grid', gap: 1.5 }}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Eğer hesap mevcutsa, şifre yenileme bağlantısı{' '}
                {masked ? <b>{masked}</b> : 'e-postana'} gönderildi.
              </Alert>

              <MDTypography variant="caption" color="text" sx={{ opacity: 0.75, lineHeight: 1.6 }}>
                E-posta gelmediyse:
                <br />• Spam/Junk klasörünü kontrol et
                <br />• 1-2 dakika bekleyip tekrar dene
                <br />• Yanlış e-posta/kullanıcı adı yazmış olabilirsin
              </MDTypography>

              <Divider />

              <MDButton
                component={Link}
                to="/sign-in"
                variant="gradient"
                color="info"
                fullWidth
                sx={{
                  py: 1.2,
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}
              >
                Giriş ekranına dön
              </MDButton>

              <MDButton
                variant="outlined"
                color="info"
                fullWidth
                onClick={() => {
                  setDone(false);
                  setErr(null);
                }}
                sx={{ borderRadius: '14px', textTransform: 'none' }}
              >
                Tekrar dene
              </MDButton>
            </MDBox>
          ) : (
            <MDBox
              component="form"
              role="form"
              onSubmit={onSubmit}
              sx={{ display: 'grid', gap: 2 }}
            >
              <MDInput
                type="text"
                label="E-posta veya kullanıcı adı"
                fullWidth
                autoFocus
                value={loginId}
                disabled={loading}
                onChange={(e) => setLoginId(e.target.value)}
                autoComplete="username"
                inputProps={{ inputMode: 'text' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon fontSize="small">person</Icon>
                    </InputAdornment>
                  ),
                }}
              />

              {err && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {err}
                </Alert>
              )}

              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={!canSubmit}
                sx={{
                  mt: 0.25,
                  py: 1.3,
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}
              >
                {loading ? (
                  <MDBox display="flex" alignItems="center" justifyContent="center" gap={1}>
                    <CircularProgress size={16} thickness={5} />
                    <span>Bağlantı gönderiliyor…</span>
                  </MDBox>
                ) : (
                  'Şifre yenileme bağlantısı gönder'
                )}
              </MDButton>

              <MDTypography
                variant="button"
                color="text"
                sx={{ opacity: 0.8, textAlign: 'center', mt: 0.25 }}
              >
                Şifreni hatırladın mı?{' '}
                <MDTypography
                  component={Link}
                  to="/sign-in"
                  variant="button"
                  color="info"
                  fontWeight="bold"
                  sx={{ textDecoration: 'none' }}
                >
                  Giriş yap
                </MDTypography>
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </Card>
    </AuthBasicLayout>
  );
}
